/**
 * @module HypothesisMatchedObservables
 * @purpose Renders present-evidence entries for a hypothesis card.
 * @depends types/reasoning (HypothesisPresentEntry), react-i18next
 * @dependedOnBy HypothesisCard
 *
 * @rules
 * - Performance: pure display — props only, no state
 * - State: none
 * - Errors: n/a — purely presentational; observation ID list is truncated at MAX_VISIBLE_OBS_IDS
 */

import { useTranslation } from 'react-i18next'
import type { HypothesisPresentEntry } from '../../../../../types/reasoning'

interface HypothesisMatchedObservablesProps {
  entries: HypothesisPresentEntry[]
}

interface MatchedEntryProps {
  entry: HypothesisPresentEntry
}

const MAX_VISIBLE_OBS_IDS = 3

const CONTAINER_STYLE: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  padding: '10px 0',
}

const SECTION_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: '6px',
}

const OBSERVABLE_ID_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.72rem',
  color: 'var(--text)',
  marginBottom: '2px',
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginRight: '4px',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

const OBS_ID_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
  marginRight: '4px',
  display: 'inline-block',
}

const EMPTY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
}

const ENTRY_CONTAINER: React.CSSProperties = {
  padding: '6px 0',
  borderBottom: '1px solid var(--border)',
}

const META_ROW: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.75rem',
  color: 'var(--text-dim)',
  marginTop: '2px',
}

const TEMPORAL_NOTE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
  marginTop: '2px',
}

const EXECUTION_ASSERTIONS_WITH_EVIDENCE = new Set(['recorded', 'supported'])

function MatchedEntry({ entry }: MatchedEntryProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const visibleIds = entry.observation_ids.slice(0, MAX_VISIBLE_OBS_IDS)
  const hiddenCount = entry.observation_ids.length - visibleIds.length
  const showExecutionEvidence = entry.execution_assertion !== undefined
    && EXECUTION_ASSERTIONS_WITH_EVIDENCE.has(entry.execution_assertion)

  return (
    <div style={ENTRY_CONTAINER}>
      <div style={OBSERVABLE_ID_STYLE}>{entry.observable}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
        <span style={CHIP_BASE}>{t(`canvas.artefactTypes.${entry.artefact_type}` as never, { defaultValue: entry.artefact_type })}</span>
        <span style={CHIP_BASE}>{t(`reasoning.hypothesis.matchedObservables.temporal.${entry.temporal}` as never, { defaultValue: entry.temporal })}</span>
        <span style={CHIP_BASE}>{t(`reasoning.hypothesis.matchedObservables.window.${entry.matched}` as never, { defaultValue: entry.matched })}</span>
        {showExecutionEvidence && (
          <span style={CHIP_BASE}>{t('reasoning.hypothesis.matchedObservables.executionEvidence')}</span>
        )}
      </div>
      <div style={META_ROW}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-muted)', marginRight: '4px' }}>
          {t('reasoning.hypothesis.matchedObservables.observationIds', { count: entry.observation_ids.length })}:
        </span>
        {visibleIds.map((id) => <span key={id} style={OBS_ID_STYLE}>{id}</span>)}
        {hiddenCount > 0 && (
          <span style={OBS_ID_STYLE}>
            {t('reasoning.hypothesis.matchedObservables.andMore', { count: hiddenCount })}
          </span>
        )}
      </div>
      {entry.temporal_constraint_met !== undefined && (
        <div style={TEMPORAL_NOTE}>
          {entry.temporal_constraint_met
            ? t('reasoning.hypothesis.matchedObservables.temporalConstraintMet')
            : t('reasoning.hypothesis.matchedObservables.temporalConstraintNotMet')}
        </div>
      )}
      {entry.matched_window_field !== undefined && (
        <div style={TEMPORAL_NOTE}>
          {t('reasoning.hypothesis.matchedObservables.matchedWindowField', { field: entry.matched_window_field })}
        </div>
      )}
    </div>
  )
}

/**
 * Renders the matched-observable evidence inventory for a hypothesis.
 *
 * @behaviour Shows empty message when no present entries; otherwise renders one row per entry
 *   with observable ID, artefact-type badge, temporal badge, matched value, and truncated observation IDs.
 * @errors n/a
 */
export function HypothesisMatchedObservables({ entries }: HypothesisMatchedObservablesProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  return (
    <div style={CONTAINER_STYLE}>
      <div style={SECTION_HEADING}>{t('reasoning.hypothesis.matchedObservables.title')}</div>
      {entries.length === 0
        ? <div style={EMPTY_STYLE}>{t('reasoning.hypothesis.matchedObservables.empty')}</div>
        : entries.map((entry) => <MatchedEntry key={entry.observable} entry={entry} />)
      }
    </div>
  )
}
